import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";

const mapElements = document.querySelectorAll(".address-map");
const searchForm = document.getElementById("location-search-form");
const locationInput = document.getElementById("location-input");
const resetButton = document.getElementById("reset-map");
const productCheckboxes = document.querySelectorAll(".checkbox-input");
const dealerListContainer = document.querySelector("#dealer-list-container");

let firstLoad = true;
let userCountry = null;

// Detect user country for postal code search prioritization
fetch("https://api.country.is")
    .then((res) => res.json())
    .then((data) => {
        userCountry = data.country?.toLowerCase() || null;
    })
    .catch(() => {});

if (mapElements.length > 0) {
    const mapContainer = document.querySelector("#map-dealers");

    if (mapContainer) {
        const map = L.map(mapContainer, {
            center: [0, 0],
            zoom: 4,
            zoomSnap: 0.5,
            scrollWheelZoom: false,
            zoomControl: false,
        });

        L.control.zoom({ position: "bottomright" }).addTo(map);
        map.attributionControl.setPrefix(false);

        // Esri Light Gray Canvas: CARTO Positron now needs an API key (watermark on every tile)
        L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}", {
            maxNativeZoom: 16, // no real tile data above 16, upscale instead of "Map data not yet available"
            maxZoom: 20,
            attribution: 'Tiles &copy; <a href="https://www.esri.com">Esri</a> &mdash; Esri, HERE, Garmin, &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(map);

        const markerIcon = L.divIcon({
            className: "",
            html: '<div class="map-marker"></div>',
            iconSize: [30, 35],
            iconAnchor: [15, 35],
        });

        const clusterer = L.markerClusterGroup({
            maxClusterRadius: 60,
            showCoverageOnHover: false,
            iconCreateFunction: (cluster) => {
                const count = cluster.getChildCount();
                return L.divIcon({
                    className: "",
                    html: `<div class="h-2 w-2 rounded-full flex items-center justify-center bg-accent hover:bg-black text-white">${count}</div>`,
                    iconSize: [24, 24],
                });
            },
        });
        map.addLayer(clusterer);

        const bounds = L.latLngBounds([]);
        const markers = [];
        const allDealers = [];
        const popup = L.popup({ offset: [0, -30] });

        // Dealer cards store country display names (Dutch); Nominatim needs ISO 3166-1
        // alpha-2 codes. Build the name->code map from the browser's own locale data so
        // new countries in the CMS work without touching this file.
        const COUNTRY_CODES = (() => {
            // deprecated/alias ISO codes that share a display name with the canonical
            // one (DD=East Germany, FX=Metropolitan France, UK=alias for GB, ...)
            const skip = new Set(["an", "bu", "cs", "dd", "fx", "nt", "su", "tp", "yd", "yu", "zr", "uk"]);
            const map = {};
            ["nl", "en"].forEach((locale) => {
                const displayNames = new Intl.DisplayNames([locale], { type: "region" });
                for (let a = 65; a <= 90; a++) {
                    for (let b = 65; b <= 90; b++) {
                        const code = String.fromCharCode(a) + String.fromCharCode(b);
                        if (skip.has(code.toLowerCase())) continue;
                        const name = displayNames.of(code);
                        if (name && name !== code && !(name.toLowerCase() in map)) {
                            map[name.toLowerCase()] = code.toLowerCase();
                        }
                    }
                }
            });
            return map;
        })();

        function toCountryCode(name) {
            const key = (name || "").trim().toLowerCase();
            if (/^[a-z]{2}$/.test(key)) return key; // already an ISO code
            return COUNTRY_CODES[key] || null;
        }

        // Collect dealer countries from cards
        const dealerCountries = [
            ...new Set(
                Array.from(mapElements)
                    .map((el) => toCountryCode(el.dataset.country))
                    .filter(Boolean)
            ),
        ];

        map.on("popupclose", () => {
            allDealers.forEach(({ el }) => {
                el.classList.remove("dealer-selected");
                el.style.order = "";
            });
        });

        function getSelectedProductIds() {
            return Array.from(productCheckboxes)
                .filter((cb) => (cb.type === "hidden" && cb.value) || cb.checked)
                .map((cb) => cb.value.trim());
        }

        function createInfoContent(el) {
            return `
          <div style="min-width:180px;">
            <strong class="font-bold">${el.dataset.name}</strong><br>
            ${el.dataset.addressLine},<br>
            ${el.dataset.city}<br>
            <a href="tel:${el.dataset.tel}">${el.dataset.tel}</a><br>
            <a href="mailto:${el.dataset.email}">${el.dataset.email}</a><br>
            ${el.dataset.link ? `<a href="${el.dataset.link}" target="_blank">${el.dataset.linktext}</a>` : ""}
          </div>
        `;
        }

        function attachHandlers(el, marker, lat, lng) {
            const infoContent = createInfoContent(el);

            const selectDealer = () => {
                allDealers.forEach(({ el }) => {
                    el.classList.remove("dealer-selected");
                    el.style.order = "";
                });
                el.classList.add("dealer-selected");
                el.style.order = -1;
                popup.setLatLng([lat, lng]).setContent(infoContent).openOn(map);
            };

            el.addEventListener("click", selectDealer);
            marker.on("click", selectDealer);
        }

        function createMarker(el, lat, lng) {
            const marker = L.marker([lat, lng], { icon: markerIcon });
            attachHandlers(el, marker, lat, lng);
            clusterer.addLayer(marker);
            markers.push(marker);
            return marker;
        }

        // Create all dealer markers
        mapElements.forEach((el) => {
            const lat = parseFloat(el.dataset.lat);
            const lng = parseFloat(el.dataset.lng);
            if (isNaN(lat) || isNaN(lng)) return;

            bounds.extend([lat, lng]);
            createMarker(el, lat, lng);
            allDealers.push({ el, lat, lng });
        });

        if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40] });

        function haversineDistance(lat1, lng1, lat2, lng2) {
            const R = 6371;
            const dLat = ((lat2 - lat1) * Math.PI) / 180;
            const dLng = ((lng2 - lng1) * Math.PI) / 180;
            const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
            return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        }

        function applyFilters(latCenter = null, lngCenter = null, radius = 0) {
            const selectedProducts = getSelectedProductIds();
            let filtered = allDealers.filter(({ el, lat, lng }) => {
                const inRadius = latCenter !== null ? haversineDistance(latCenter, lngCenter, lat, lng) <= radius : true;
                const dealerProducts = (el.dataset.products || "").split(",");
                const matchProducts = selectedProducts.length === 0 || selectedProducts.some((id) => dealerProducts.includes(id.trim()));
                return inRadius && matchProducts;
            });

            if (latCenter !== null && lngCenter !== null) {
                filtered.sort((a, b) => haversineDistance(latCenter, lngCenter, a.lat, a.lng) - haversineDistance(latCenter, lngCenter, b.lat, b.lng));
            }

            allDealers.forEach(({ el }) => {
                el.style.display = "none";
                el.classList.remove("dealer-selected");
                el.style.order = "";
            });

            filtered.forEach(({ el }) => (el.style.display = ""));
            if (dealerListContainer) filtered.forEach(({ el }) => dealerListContainer.appendChild(el));

            map.closePopup();
            clusterer.clearLayers();
            markers.length = 0;

            const newBounds = L.latLngBounds([]);

            filtered.forEach(({ el, lat, lng }) => {
                createMarker(el, lat, lng);
                newBounds.extend([lat, lng]);
            });

            if (newBounds.isValid()) {
                map.fitBounds(newBounds, { padding: [40, 40], maxZoom: 14 });
            }
        }

        productCheckboxes.forEach((cb) => cb.addEventListener("change", () => applyFilters()));

        // --- Geocoding via Nominatim with user-country restriction + fallback ---
        const geocodeWithFallback = (query) => {
            const allCountries = userCountry ? [userCountry, ...dealerCountries.filter((c) => c !== userCountry)] : dealerCountries;

            const tryGeocode = (countries) => {
                if (countries.length === 0) {
                    alert("Location not found.");
                    return;
                }

                const [current, ...rest] = countries;
                const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&countrycodes=${encodeURIComponent(current)}&q=${encodeURIComponent(query)}`;

                fetch(url, { headers: { Accept: "application/json" } })
                    .then((res) => res.json())
                    .then((results) => {
                        const valid =
                            results.find((r) => ["city", "town", "village", "municipality", "postcode", "suburb"].includes(r.addresstype)) ||
                            results[0];

                        if (valid) {
                            applyFilters(parseFloat(valid.lat), parseFloat(valid.lon), 20);
                        } else {
                            tryGeocode(rest);
                        }
                    })
                    .catch(() => tryGeocode(rest));
            };

            tryGeocode(allCountries);
        };

        // --- Form submit handler ---
        searchForm?.addEventListener("submit", (e) => {
            e.preventDefault();
            const query = locationInput.value.trim();
            if (!query) return;
            geocodeWithFallback(query);
        });

        resetButton?.addEventListener("click", () => {
            productCheckboxes.forEach((cb) => (cb.checked = false));
            locationInput.value = "";
            applyFilters();
        });

        // --- Init filters on load ---
        function initFiltersOnLoad() {
            const query = locationInput?.value?.trim();
            const hasHidden = firstLoad && Array.from(productCheckboxes).some((cb) => cb.type === "hidden" && cb.value);
            const hasChecked = Array.from(productCheckboxes).some((cb) => cb.checked);

            if (query) {
                geocodeWithFallback(query);
            } else if (hasHidden || hasChecked) {
                applyFilters();
            } else {
                applyFilters();
            }

            firstLoad = false;
        }

        initFiltersOnLoad();
    }
}
